import { Component, HostListener, OnDestroy, computed, effect, input, output, signal, viewChild, viewChildren, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicModule } from 'ng-dynamic-component';
import { ButtonModule } from 'primeng/button';
import { FocusTrapModule } from 'primeng/focustrap';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { DashboardWorkspaceModel, UiComponentModel } from '../../models/dashboard.model';
import { UserRoles } from '../../../user-admin-page/user-model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { GridItemResizedMessage } from 'src/app/shared/models/mediator-messages.model';
import { GridResizeHandler } from 'src/app/shared/helpers/grid-resize.helper';
import { ComponentLauncher } from '../../services/component-launcher';
import { WORKSPACE_PANEL_WIZARD_STEPS, WizardStep } from './workspace-panel-wizard.model';

function padOrTrimSizes(sizes: number[], count: number): number[] {
  const result = [...sizes];
  while (result.length < count) result.push(1);
  return result.slice(0, count);
}

@Component({
  selector: 'app-workspace-panel',
  templateUrl: './workspace-panel.component.html',
  styles: [':host { display: flex; flex-direction: column; height: 100%; overflow: hidden; }'],
  standalone: true,
  imports: [CommonModule, FormsModule, DynamicModule, ButtonModule, FocusTrapModule, SelectModule, TooltipModule]
})
export class WorkspacePanelComponent implements OnDestroy {

  readonly workspace = input.required<DashboardWorkspaceModel>();
  readonly userRoles = input<UserRoles | null>(null);
  readonly currentStringName = input('');
  readonly holeSectionName = input('');
  readonly componentInstance = input('');
  readonly panelColumnCount = input(2);
  readonly stringDependentComponents = input<string[]>([]);
  readonly panelRowSizes = input<number[]>([]);
  readonly panelColSizes = input<number[]>([]);
  readonly initialWizardMode = input<boolean>(false);
  readonly initialWizardStepIndex = input<number>(0);

  readonly componentRemoved = output<UiComponentModel>();
  readonly rowResizeEnd = output<number[]>();
  readonly colResizeEnd = output<number[]>();

  readonly wizardSteps: readonly WizardStep[] = WORKSPACE_PANEL_WIZARD_STEPS;
  readonly wizardMode = signal<boolean>(false);
  readonly wizardStepIndex = signal<number>(0);

  readonly currentWizardStep = computed<WizardStep | null>(
    () => this.wizardSteps[this.wizardStepIndex()] ?? null
  );

  readonly displayedComponents = computed<UiComponentModel[]>(() => {
    if (!this.wizardMode()) {
      return this.workspace()?.uiComponents ?? [];
    }
    const step = this.currentWizardStep();
    if (!step) return [];
    const registry = this._componentLauncher.getComponentRegistry();
    return step.componentKeys
      .map(key => {
        const comp = registry.find((c: any) => c.key === key);
        if (!comp) return null;
        return new UiComponentModel({
          id: `wizard:${key}`,
          name: comp.name,
          componentInstance: comp.componentInstance,
          cols: comp.cols,
          rows: comp.rows
        });
      })
      .filter((c): c is UiComponentModel => c !== null);
  });

  readonly panelColumns = computed(() => {
    const count = this.displayedComponents().length;
    if (count <= 1) return 1;
    if (this.wizardMode()) {
      return count <= 2 ? count : 3;
    }
    return this.panelColumnCount();
  });

  readonly panelRows = computed(() =>
    Math.ceil(this.displayedComponents().length / this.panelColumns())
  );

  readonly panelGridTemplateColumns = computed(() => {
    const c = this.panelColumns();
    let sizes: number[];
    if (this.wizardMode()) {
      const stepSizes = this.currentWizardStep()?.colSizes;
      sizes = stepSizes && stepSizes.length > 0
        ? padOrTrimSizes([...stepSizes], c)
        : Array(c).fill(1);
    } else {
      sizes = padOrTrimSizes(this._colSizes(), c);
    }
    return sizes.map(s => `${s}fr`).join(' 3px ');
  });

  readonly panelGridTemplateRows = computed(() => {
    const r = this.panelRows();
    const sizes = padOrTrimSizes(this._rowSizes(), r);
    return r <= 1 ? '1fr' : sizes.map(s => `${s}fr`).join(' 3px ');
  });

  readonly panelRowHandleIndices = computed(() =>
    Array.from({ length: Math.max(0, this.panelRows() - 1) }, (_, i) => i)
  );

  readonly panelColHandleIndices = computed(() =>
    Array.from({ length: Math.max(0, this.panelColumns() - 1) }, (_, i) => i)
  );

  private readonly _panelGrid = viewChild<ElementRef>('panelGrid');
  private readonly _panelCellContents = viewChildren<ElementRef>('panelCellContent');

  // Local mutable copies for live resize dragging
  private readonly _rowSizes = signal<number[]>([]);
  private readonly _colSizes = signal<number[]>([]);
  private readonly _resizeHandler = new GridResizeHandler();
  private _panelResizeObserver: ResizeObserver;
  private readonly _panelElementToNameMap = new Map<Element, string>();


  constructor(
    private _messenger: MediatorService,
    private _componentLauncher: ComponentLauncher
  ) {
    // Sync parent-controlled sizes to local copies when inputs change
    effect(() => this._rowSizes.set(this.panelRowSizes()));
    effect(() => this._colSizes.set(this.panelColSizes()));
    // Sync parent-persisted wizard state to local signals when inputs change
    effect(() => this.wizardMode.set(this.initialWizardMode()));
    effect(() => {
      const idx = Math.min(
        Math.max(0, this.initialWizardStepIndex()),
        this.wizardSteps.length - 1
      );
      this.wizardStepIndex.set(idx);
    });

    // Observe panel cells for height changes so child charts can react
    this._panelResizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        const name = this._panelElementToNameMap.get(entry.target);
        if (name) {
          const h = entry.contentRect.height;
          if (h > 0) this._messenger.publish(new GridItemResizedMessage(name, h, 0));
        }
      });
    });

    effect(() => {
      const list = this._panelCellContents();
      const components = this.displayedComponents();
      this._panelResizeObserver.disconnect();
      this._panelElementToNameMap.clear();
      if (list && list.length > 0) {
        list.forEach((ref, idx) => {
          const component = components[idx];
          if (component) {
            this._panelElementToNameMap.set(ref.nativeElement, component.name);
            this._panelResizeObserver.observe(ref.nativeElement);
          }
        });
        // Force layout calculation after grid renders - defer to avoid NG0100
        setTimeout(() => {
          requestAnimationFrame(() => {
            list.forEach((ref, idx) => {
              const component = components[idx];
              if (component && ref.nativeElement) {
                const rect = ref.nativeElement.getBoundingClientRect();
                if (rect.height > 0) {
                  this._messenger.publish(new GridItemResizedMessage(component.name, rect.height, 0));
                }
              }
            });
          });
        }, 0);
      }
    });
  }

  getItemGridRow(index: number): number {
    return Math.floor(index / this.panelColumns()) * 2 + 1;
  }

  getPanelItemGridColumn(index: number): string {
    const cols = this.panelColumns();
    const col = index % cols;
    const start = col * 2 + 1;
    const count = this.displayedComponents().length;
    const rows = this.panelRows();
    const isLastRow = index >= (rows - 1) * cols;
    const itemsInLastRow = count % cols;
    if (isLastRow && itemsInLastRow !== 0 && index === count - 1) {
      const colSpan = cols - itemsInLastRow + 1;
      return `${start} / span ${colSpan * 2 - 1}`;
    }
    return `${start}`;
  }

  startRowResize(event: MouseEvent, rowIdx: number): void {
    const sizes = padOrTrimSizes(this._rowSizes(), this.panelRows());
    this._resizeHandler.startRowResize(event, rowIdx, sizes, this._panelGrid()!.nativeElement.clientHeight);
  }

  startColResize(event: MouseEvent, colIdx: number): void {
    const sizes = padOrTrimSizes(this._colSizes(), this.panelColumns());
    this._resizeHandler.startColResize(event, colIdx, sizes, this._panelGrid()!.nativeElement.clientWidth);
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    const newRowSizes = this._resizeHandler.computeRowResize(event, this.panelRows());
    if (newRowSizes) { this._rowSizes.set(newRowSizes); return; }
    const newColSizes = this._resizeHandler.computeColResize(event, this.panelColumns());
    if (newColSizes) this._colSizes.set(newColSizes);
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    if (this._resizeHandler.endRowResize()) {
      this.rowResizeEnd.emit(this._rowSizes());
    } else if (this._resizeHandler.endColResize()) {
      this.colResizeEnd.emit(this._colSizes());
    }
  }

  ngOnDestroy(): void {
    this._panelResizeObserver.disconnect();
  }
}
